// src/components/OrdersFilterModal.js
import React, { useState, useEffect } from "react";
import "./OrdersFilterModal.css";

// Domyślne opcje dla checkboxów w grupach: priorytet, rodzaj zleceń, status
const defaultCheckboxOptions = {
  waga: ["Bardzo Niski", "Niski", "Neutralny", "Wysoki", "Krytyczny"],
  rodzaj_zgloszenia: ["Usługa","Przegląd A","Wewnętrzne","Przegląd Z","Reklamacja","Awaria"],
  status: ["Oczekujące", "Przydzielone", "Realizowane", "Wstrzymane", "Walidacja", "Fakturowanie", "Zatwierdzone"],
};

// Komponent renderujący rozwijalną grupę checkboxów
function CollapsibleCheckboxGroup({
  field,
  label,
  options,
  selected,
  onCheckboxChange,
  optionFormatter,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  return (
    <div className="collapsible-group">
      <div className="collapsible-header" onClick={toggleExpanded}>
        <span className="filter-label">{label}:</span>
        <span className="collapsible-icon">{isExpanded ? "−" : "+"}</span>
      </div>
      {isExpanded && (
        <div className="checkbox-group collapsible-content">
          {options.map((option) => {
            // Jeśli opcja jest obiektem, oczekujemy własności "id". Jeśli nie – traktujemy jako prostą wartość.
            const value = option.id ? String(option.id) : option;
            const displayText = optionFormatter ? optionFormatter(option) : option;
            return (
              <label key={value} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={(e) => onCheckboxChange(e, field, option)}
                />
                {displayText}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrdersFilterModal({
  initialFilters,
  onSavePermanent,
  onSaveTemporary,
  onClear,
  onCancel,
  // Listy opcji przekazywane z OrdersPage (dane dynamiczne z bazy)
  userOptions = {
    osoba_wprowadzajaca_id: [],
    osoba_wykonujaca_id: [],
    osoba_walidujaca_id: [],
    osoba_zamykajaca_id: [],
  },
  areaOptions = [],
  clientOptions = [],
}) {
  const [localFilters, setLocalFilters] = useState(initialFilters);

  // Jeżeli initialFilters się zmienią (np. poprzez czyszczenie), zaktualizuj stan lokalny
  useEffect(() => {
    setLocalFilters(initialFilters);
  }, [initialFilters]);

  // Handler dla pól tekstowych i dat (zakresowych)
  const handleChange = (e, field, subfield) => {
    const value = e.target.value;
    setLocalFilters((prev) => ({
      ...prev,
      [field]: { ...prev[field], [subfield]: value },
    }));
  };

  // Handler dla checkboxów w rozwijalnych grupach
  const handleCheckboxChange = (e, field, option) => {
    const isChecked = e.target.checked;
    const value = option.id ? String(option.id) : option;
    setLocalFilters((prev) => {
      const current = prev[field].selected || [];
      const newSelected = isChecked
        ? [...current, value]
        : current.filter((item) => item !== value);
      return { ...prev, [field]: { ...prev[field], selected: newSelected } };
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3 className="modal-header">Filtry zleceń</h3>
        <form className="filters-form">
          {/* Grupa pełnej szerokości dla pól zakresowych */}
          <div className="full-width-group">
            {/* Nr.ref – zakres tekstowy */}
            <div className="filter-group">
              <label className="filter-label">Nr.ref:</label>
              <div className="range-group">
                <input
                  type="text"
                  placeholder="Od (np. 0100/2025)"
                  value={localFilters.nrRef.from}
                  onChange={(e) => handleChange(e, "nrRef", "from")}
                  className="range-input"
                />
                <input
                  type="text"
                  placeholder="Do (np. 0200/2025)"
                  value={localFilters.nrRef.to}
                  onChange={(e) => handleChange(e, "nrRef", "to")}
                  className="range-input"
                />
              </div>
            </div>
          </div>

          {/* Pierwsza sekcja rozwijalnych grup checkboxów – Priorytet, Rodzaj zleceń, Status */}
          <div className="collapsible-groups-container">
            <CollapsibleCheckboxGroup
              field="waga"
              label="Priorytet"
              options={defaultCheckboxOptions.waga}
              selected={localFilters.waga.selected}
              onCheckboxChange={handleCheckboxChange}
            />
            <CollapsibleCheckboxGroup
              field="rodzaj_zgloszenia"
              label="Rodzaj zleceń"
              options={defaultCheckboxOptions.rodzaj_zgloszenia}
              selected={localFilters.rodzaj_zgloszenia.selected}
              onCheckboxChange={handleCheckboxChange}
            />
            <CollapsibleCheckboxGroup
              field="status"
              label="Status"
              options={defaultCheckboxOptions.status}
              selected={localFilters.status.selected}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>

          {/* Grupa pełnej szerokości dla dat */}
          <div className="full-width-group">
            <div className="filter-group">
              <label className="filter-label">Data zgłoszenia:</label>
              <div className="range-group">
                <input
                  type="date"
                  value={localFilters.data_zlecenia.from}
                  onChange={(e) => handleChange(e, "data_zlecenia", "from")}
                  className="range-input"
                />
                <input
                  type="date"
                  value={localFilters.data_zlecenia.to}
                  onChange={(e) => handleChange(e, "data_zlecenia", "to")}
                  className="range-input"
                />
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">DeadLine:</label>
              <div className="range-group">
                <input
                  type="date"
                  value={localFilters.deadline.from}
                  onChange={(e) => handleChange(e, "deadline", "from")}
                  className="range-input"
                />
                <input
                  type="date"
                  value={localFilters.deadline.to}
                  onChange={(e) => handleChange(e, "deadline", "to")}
                  className="range-input"
                />
              </div>
            </div>
          </div>

          {/* Druga sekcja rozwijalnych grup checkboxów – pola związane z tabelami */}
          <div className="collapsible-groups-container">
            <CollapsibleCheckboxGroup
              field="osoba_wprowadzajaca_id"
              label="Zgłaszający"
              options={userOptions.osoba_wprowadzajaca_id}
              selected={localFilters.osoba_wprowadzajaca_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(user) => `${user.first_name} ${user.last_name}`}
            />
            <CollapsibleCheckboxGroup
              field="osoba_wykonujaca_id"
              label="Przydzielony"
              options={userOptions.osoba_wykonujaca_id}
              selected={localFilters.osoba_wykonujaca_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(user) => `${user.first_name} ${user.last_name}`}
            />
            <CollapsibleCheckboxGroup
              field="osoba_walidujaca_id"
              label="Walidujący"
              options={userOptions.osoba_walidujaca_id}
              selected={localFilters.osoba_walidujaca_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(user) => `${user.first_name} ${user.last_name}`}
            />
            <CollapsibleCheckboxGroup
              field="osoba_zamykajaca_id"
              label="Zamykający"
              options={userOptions.osoba_zamykajaca_id}
              selected={localFilters.osoba_zamykajaca_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(user) => `${user.first_name} ${user.last_name}`}
            />
            <CollapsibleCheckboxGroup
              field="obszar_id"
              label="Obszar"
              options={areaOptions}
              selected={localFilters.obszar_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(area) => area.name}
            />
            <CollapsibleCheckboxGroup
              field="klient_id"
              label="Klient"
              options={clientOptions}
              selected={localFilters.klient_id.selected}
              onCheckboxChange={handleCheckboxChange}
              optionFormatter={(client) => client.name}
            />
          </div>
        </form>
        <div className="modal-buttons">
          <button className="btn-submit" type="button" onClick={() => onSavePermanent(localFilters)}>
            Zapamiętaj Filtry
          </button>
          <button className="btn-submit" type="button" onClick={() => onSaveTemporary(localFilters)}>
            Zapisz bez zapamiętywania
          </button>
          <button className="btn-submit" type="button" onClick={() => onClear()}>
            Wyczyść Filtry
          </button>
          <button className="btn-submit" type="button" onClick={onCancel}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrdersFilterModal;
